import { isCancelledError, useQueryErrorResetBoundary } from '@tanstack/react-query'
import { type ErrorComponentProps, Link, useRouter } from '@tanstack/react-router'
import { FileQuestion } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { isApiError } from '../lib/api'
import { Button } from '../ui/button'
import { EmptyState, ErrorState, Skeleton } from '../ui/feedback'
import { Page } from '../ui/layout'

/**
 * A route failed to load or render: say what happened. Retrying helps with network and server
 * failures; for a missing record or a missing permission it cannot, so offer a way home instead.
 */
/** Automatic retries after a cancelled load, per page load: a safety net, never a loop. */
let cancelledRetries = 0

export function RouteError({ error, reset }: ErrorComponentProps) {
  const { t } = useTranslation()
  const router = useRouter()
  const queryReset = useQueryErrorResetBoundary()
  // A request cancelled mid-flight (its observer went away) is not an error: load again quietly.
  const cancelled = isCancelledError(error) && cancelledRetries < 5

  useEffect(() => {
    queryReset.reset()
    if (cancelled) {
      cancelledRetries += 1
      reset()
      void router.invalidate()
    }
  }, [queryReset, cancelled, reset, router])

  if (cancelled) return <PagePending />

  const status = isApiError(error) ? error.status : 0
  const answered = status === 403 || status === 404
  return (
    <Page>
      <ErrorState
        error={error}
        title={
          status === 404
            ? t('errors.notFoundTitle')
            : status === 403
              ? // A switched-off module is not a missing permission (ADR-024).
                isApiError(error) && error.code === 'MODULE_DISABLED'
                ? t('errors.moduleDisabledTitle')
                : t('errors.forbiddenTitle')
              : undefined
        }
        action={
          answered ? (
            <Button asChild>
              <Link to="/">{t('errors.goHome')}</Link>
            </Button>
          ) : undefined
        }
        onRetry={() => {
          reset()
          void router.invalidate()
        }}
      />
    </Page>
  )
}

export function NotFound() {
  const { t } = useTranslation()
  return (
    <Page>
      <EmptyState
        icon={<FileQuestion />}
        title={t('errors.notFoundPage')}
        description={t('errors.notFoundPageBody')}
        action={
          <Button asChild>
            <Link to="/">{t('errors.goHome')}</Link>
          </Button>
        }
      />
    </Page>
  )
}

/** Shown only when a route's data takes noticeably long (router default: after 1s). */
export function PagePending() {
  const { t } = useTranslation()
  return (
    <Page role="status" aria-busy="true" aria-label={t('loading')}>
      <Skeleton className="h-6 w-56" />
      <Skeleton className="mt-3 h-3 w-40" />
      <div className="mt-6 flex flex-col gap-3">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    </Page>
  )
}

/**
 * A profile (customer, supplier) still loading on a cold start: skeletons in the profile's own
 * shape (identity band, ledger strip, tab rail), so nothing jumps when it arrives.
 */
export function ProfilePending() {
  const { t } = useTranslation()
  return (
    <Page role="status" aria-busy="true" aria-label={t('loading')} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-6 w-72" />
          <Skeleton className="h-3 w-56" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-8 w-8" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-rule sm:grid-cols-4 2xl:grid-cols-[1.5fr_1.5fr_1fr_1fr_1fr_1fr]">
        {['a', 'b', 'c', 'd', 'e', 'f'].map((key, index) => (
          <div
            key={key}
            className={
              index < 2
                ? 'flex flex-col gap-2 px-4 py-3 sm:col-span-2 2xl:col-span-1'
                : 'flex flex-col gap-2 px-4 py-3'
            }
          >
            <Skeleton className="h-2.5 w-20" />
            <Skeleton className={index < 2 ? 'h-6 w-32' : 'h-4 w-24'} />
            <Skeleton className="h-2.5 w-16" />
          </div>
        ))}
      </div>
      <div className="flex gap-5 border-b border-rule pb-2">
        {['a', 'b', 'c', 'd', 'e'].map((key) => (
          <Skeleton key={key} className="h-3 w-20" />
        ))}
      </div>
    </Page>
  )
}
